/**
 * /play — คอนโทรลเลอร์ที่ **ชุดปุ่มเปลี่ยนตามฐานที่ยืนอยู่**
 * นี่คือสิ่งที่จอย NES ตัวเดียวทำไม่ได้ และเป็นเหตุผลที่เลือกมือถือเป็นคอนโทรลเลอร์
 *
 * หน้าตาอิงจาก `mockups/player_you_controller.html` ทั้งชุด:
 * แถบสถานะ + การ์ดตัวละคร + แถบบอกฐาน 6 ขั้น + สติ๊กอนาล็อก + ปุ่ม A กลม
 */
import {
  AVATAR, FRAMES_PER_SHEET, MODE_BY_SCENE, SCENE_IDS, SCENE_NAME, STYLE_BY_SCENE,
  type SceneId,
} from "@py/protocol";

import { Controller } from "../controller.js";
import { ENV } from "../env.js";
import { loadSession, profileFromSession, saveSession } from "../session.js";

/** คำอธิบายว่าฐานนี้ต้องทำอะไร — ช่วยผู้ชมที่เพิ่งเดินมาถึง */
const HOW: Record<SceneId, string> = {
  0: "ลากสติ๊กซ้าย-ขวาเพื่อเดิน แล้วมุ่งไปทางขวาเพื่อเข้าฐาน 1",
  1: "กระโดดขึ้นอิฐ เก็บเหรียญ แล้วไปต่อทางขวา",
  2: "เขาวงกตทางเดินแคบ เดิน 4 ทิศ ทะลุขวาเพื่อไปต่อ",
  3: "ป้ายรถเมล์นีออน เดินผ่านเมืองไปทางขวา",
  4: "ป่าวิญญาณ กระโดดขึ้นกิ่งไม้ · กด ↓ พร้อมกระโดดเพื่อลงจากกิ่ง",
  5: "หมู่บ้านซากุระ กระโดดขึ้นหลังคา ต่อยเตะได้ตามใจ",
};

/** ระยะที่ต้องลากพ้นกลางสติ๊กก่อนจะนับว่ากดทิศนั้น — กันสั่นตอนนิ้วนิ่ง */
const DEADZONE = 16;
const STICK_RADIUS = 46;

export function renderPlay(root: HTMLElement): (() => void) | void {
  const s = loadSession();
  if (!s) {
    root.innerHTML = `
      <div class="title">PLAYER YOU</div>
      <div class="kick">NO CHARACTER YET</div>
      <div class="lbl">ยังไม่มีตัวละคร</div>
      <div class="note">สแกน QR ที่จอลงทะเบียนก่อนครับ</div>`;
    return;
  }
  if (s.status === "ready" && (s.aiStatus ?? "done") === "pending") {
    // ตัวละครฉบับจริงยังไม่เสร็จ — เข้าเล่นตอนนี้จะเห็นภาพร่างบนจอใหญ่
    root.innerHTML = `
      <div class="title">PLAYER YOU</div>
      <div class="kick">STILL DRAWING</div>
      <div class="lbl">AI กำลังวาดตัวละคร</div>
      <div class="note">อีกไม่เกินครึ่งนาที กลับไปดูที่หน้า “ตัวละครของฉัน”</div>
      <div style="margin-top:18px"><button class="primary" id="back">ไปดูสถานะ</button></div>`;
    root.querySelector("#back")!.addEventListener("click", () => { location.href = "/me"; });
    return;
  }
  if (s.status !== "ready") {
    root.innerHTML = `
      <div class="title">PLAYER YOU</div>
      <div class="kick">STILL DRAWING</div>
      <div class="lbl">ตัวละครยังไม่พร้อม</div>
      <div class="note">กลับไปหน้า “ตัวละครของฉัน” เพื่อดูสถานะ</div>
      <div style="margin-top:18px"><button class="primary" id="back">ไปดูสถานะ</button></div>`;
    root.querySelector("#back")!.addEventListener("click", () => { location.href = "/me"; });
    return;
  }

  document.body.classList.add("playing");

  root.innerHTML = `
    <div class="pad" id="pad">
      <div class="top">
        <div class="link">
          <span class="dot" id="dot"></span>
          <span id="conn">กำลังต่อ…</span>
          <span style="flex:1"></span>
          <span>SCORE <b id="score">0</b></span>
        </div>
        <div class="who">
          <span class="frame" id="faceframe"><img id="face" alt="ตัวละครของคุณ" /></span>
          <div>
            <div class="nm">${s.nickname ? s.nickname : "PLAYER"}</div>
            <div class="sub" id="pid">${s.playerId}</div>
          </div>
        </div>
      </div>

      <div class="where">
        <div class="wtitle">YOU ARE AT</div>
        <div class="bar" id="bar">${SCENE_IDS.map((n) => `<i data-s="${n}"></i>`).join("")}</div>
        <div class="bname" id="bname">—</div>
        <div class="bwhy" id="bwhy"></div>
      </div>

      <div class="deck" id="deck">
        <div class="hintz" id="hintz"></div>
        <div class="stickbase" id="stick">
          <div class="knob" id="knob"></div>
          <span class="arrow ar-u" id="ar-u">▲</span>
          <span class="arrow ar-d" id="ar-d">▼</span>
          <span class="arrow ar-l" id="ar-l">◀</span>
          <span class="arrow ar-r" id="ar-r">▶</span>
        </div>
        <button class="dbtn off" id="down">↓</button>
        <button class="bbtn" id="punch">ต่อย<small>B</small></button>
        <button class="cbtn" id="kick">เตะ<small>C</small></button>
        <button class="abtn" id="jump">กระโดด<small>A</small></button>
        <div class="readout" id="readout">—</div>
      </div>
    </div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  const pad = $("pad");
  const stick = $("stick");
  const knob = $("knob");
  const jumpBtn = $<HTMLButtonElement>("jump");
  const downBtn = $<HTMLButtonElement>("down");
  const punchBtn = $<HTMLButtonElement>("punch");
  const kickBtn = $<HTMLButtonElement>("kick");
  const arrows = {
    u: $("ar-u"), d: $("ar-d"), l: $("ar-l"), r: $("ar-r"),
  };

  const ctrl = new Controller(
    s,
    { url: ENV.mqttUrl, username: ENV.mqttUser, password: ENV.mqttPass },
    {
      onStatus: (st) => {
        $("conn").textContent =
          st === "online" ? "เชื่อมต่อแล้ว" : st === "connecting" ? "กำลังต่อ…" : "หลุด — ต่อใหม่";
        $("dot").classList.toggle("live", st === "online");
      },
      onPos: (sceneId, x) => {
        if (sceneId !== currentScene) {
          currentScene = sceneId;
          layout(sceneId);
        }
        $("readout").innerHTML = `X <b>${Math.round(x)}</b> · ฐาน <b>${sceneId}</b>`;
        saveSession(s);
      },
      onAct: () => {
        $("score").textContent = String(s.score);
        if (navigator.vibrate) navigator.vibrate(12);
      },
      onScreens: (up) => {
        for (const el of root.querySelectorAll<HTMLElement>("#bar i")) {
          const n = Number(el.dataset.s) as SceneId;
          el.classList.toggle("up", up.has(n) && n !== currentScene);
        }
      },
    },
  );

  let currentScene: SceneId = s.scene;
  // ผูกไว้เป็นตัวแปรใหม่: TS ไม่คงการ narrow ของ `s` เข้าไปใน function declaration ที่ถูก hoist
  const sess = s;

  /** รูปตัวละครแบบที่ใช้ในฉากปัจจุบัน — ครอปเหลือเฟรม idle เฟรมเดียว */
  const FACE_H = 58;
  function setFace(sceneId: SceneId): void {
    const style = STYLE_BY_SCENE[sceneId];
    const url = sess.urls?.[`s${style}` as "s1" | "s2" | "s3"];
    if (!url) return;
    const [fw, fh] = AVATAR[style];
    const box = $("faceframe");
    box.style.width = `${Math.round((fw / fh) * FACE_H)}px`;
    // ต้องบอก CSS ว่า sheet มีกี่เฟรม ไม่งั้นใช้ค่าเริ่มต้น 5 แล้วเห็นตัวละครซ้อนกันหลายตัว
    box.style.setProperty("--frames", String(FRAMES_PER_SHEET));
    $<HTMLImageElement>("face").src = url;
  }

  // ---------- สติ๊ก ----------
  let stickId: number | null = null;
  /**
   * จุดกลางก้าน **ยึดตอนนิ้วแตะครั้งแรก** — ห้ามอ่าน rect ใหม่ทุก pointermove
   *
   * ตอนเดินข้ามฐาน layout() เปลี่ยนรูปทรงแผงปุ่ม (floor = ก้านแบน · topdown =
   * ก้านกลม ปุ่มโผล่/หาย) ตำแหน่งก้านบนจอจึงขยับทั้งที่นิ้วยังจิ้มที่เดิม
   * ถ้าคำนวณทิศจาก rect ใหม่ นิ้วที่ลากขวาอยู่จะกลายเป็นทแยงลงทันที
   * (เคยเห็นตัวละครไถลลงมุมล่างจอหลังข้ามเข้าฐาน 1 เพราะเรื่องนี้)
   */
  let baseX = 0;
  let baseY = 0;

  const applyStick = (dx: number, dy: number, horizOnly: boolean): void => {
    const len = Math.hypot(dx, dy);
    const k = len > STICK_RADIUS ? STICK_RADIUS / len : 1;
    knob.style.transform = `translate(calc(-50% + ${dx * k}px), calc(-50% + ${dy * k}px))`;

    const l = dx < -DEADZONE ? 1 : 0;
    const r = dx > DEADZONE ? 1 : 0;
    const u = !horizOnly && dy < -DEADZONE ? 1 : 0;
    const d = !horizOnly && dy > DEADZONE ? 1 : 0;
    ctrl.press("l", l === 1);
    ctrl.press("r", r === 1);
    ctrl.press("u", u === 1);
    ctrl.press("d", d === 1);
    arrows.l.classList.toggle("a", l === 1);
    arrows.r.classList.toggle("a", r === 1);
    arrows.u.classList.toggle("a", u === 1);
    arrows.d.classList.toggle("a", d === 1);
  };

  const resetStick = (): void => {
    stickId = null;
    stick.classList.remove("live");
    knob.style.transform = "translate(-50%, -50%)";
    ctrl.press("l", false);
    ctrl.press("r", false);
    ctrl.press("u", false);
    ctrl.press("d", false);
    for (const a of Object.values(arrows)) a.classList.remove("a");
  };

  stick.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    stickId = e.pointerId;
    const b = stick.getBoundingClientRect();
    baseX = b.left + b.width / 2;
    baseY = b.top + b.height / 2;
    stick.setPointerCapture(e.pointerId);
    stick.classList.add("live");
    move(e);
  });
  const move = (e: PointerEvent) => {
    if (stickId !== e.pointerId) return;
    applyStick(e.clientX - baseX, e.clientY - baseY, pad.classList.contains("horizonly"));
  };
  stick.addEventListener("pointermove", move);
  for (const ev of ["pointerup", "pointercancel", "pointerleave"] as const) {
    stick.addEventListener(ev, resetStick);
  }

  // ---------- ปุ่ม A / ↓ ----------
  const bindHold = (el: HTMLElement, btn: "j" | "d" | "p" | "k") => {
    const set = (down: boolean) => {
      ctrl.press(btn, down);
      el.classList.toggle("hit", down);
    };
    el.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      set(true);
    });
    for (const ev of ["pointerup", "pointercancel", "pointerleave"] as const) {
      el.addEventListener(ev, () => set(false));
    }
  };
  bindHold(jumpBtn, "j");
  bindHold(downBtn, "d");
  bindHold(punchBtn, "p");
  bindHold(kickBtn, "k");

  /** ชุดปุ่มเปลี่ยนตามโหมดของฐาน */
  function layout(sceneId: SceneId): void {
    const mode = MODE_BY_SCENE[sceneId];
    // ห้าม resetStick() ตอนนิ้วยังจิ้มก้านอยู่ — มันเคลียร์ stickId แล้ว pointermove
    // ที่ตามมาจะถูกทิ้งทั้งหมด ผู้เล่นต้องยกนิ้วแล้วจิ้มใหม่ถึงจะขยับต่อได้
    // อาการที่เจอคือ "ข้ามฐานแล้วตัวละครค้างอยู่ริมจอ" (เดินค้างไว้จะเจอทุกครั้ง)
    const stillHolding = stickId !== null;
    if (!stillHolding) resetStick();

    // ฉาก 0 เดินซ้ายขวาอย่างเดียว → สติ๊กแบนกว้าง ไม่มีลูกศรขึ้นลง
    pad.classList.toggle("horizonly", mode === "floor");
    // เจ้าของงานสั่ง (ข้อ 5): กระโดดได้ทุกฉาก — ฉากมองบนเป็น hop เชิงภาพ
    // ปุ่ม A จึงโชว์ตลอด ไม่ซ่อนตามโหมดอีกแล้ว
    // ฐาน 4-5 มีกิ่ง/ชั้น ต้องมีปุ่มลงแยก เพราะกด ↓ บนสติ๊กพร้อมกระโดดด้วยนิ้วเดียวไม่ได้
    downBtn.classList.toggle("off", sceneId < 4);

    $("bname").textContent = `${sceneId} · ${SCENE_NAME[sceneId]}`;
    $("bwhy").textContent = HOW[sceneId];
    $("hintz").textContent =
      mode === "topdown" ? "ลาก 4 ทิศ · A เด้ง · B ต่อย · C เตะ"
      : mode === "floor" ? "ลากซ้าย-ขวา · A กระโดด · B C แอ็กชัน"
      : "ลากเดิน · A กระโดด · B ต่อย · C เตะ";

    for (const el of root.querySelectorAll<HTMLElement>("#bar i")) {
      el.classList.toggle("on", Number(el.dataset.s) === sceneId);
    }
    setFace(sceneId);
    // ทิศที่กดค้างอยู่คงเดิมข้ามรอยต่อ — จุดกลางก้านยึดตอนแตะ ไม่ขึ้นกับ layout ใหม่
  }

  layout(currentScene);
  // ต้องส่ง profile จริง ไม่ใช่ null — ไม่งั้นจอไม่รู้ URL ตัวละครแล้ววาดเงาดำแทน
  ctrl.start(profileFromSession(s));

  // กลับมาโฟกัสหน้าจอแล้วปุ่มต้องไม่ค้าง (ผู้ชมสลับแอปบ่อย)
  const onHide = () => {
    resetStick();
    ctrl.releaseAll();
    jumpBtn.classList.remove("hit");
    downBtn.classList.remove("hit");
    punchBtn.classList.remove("hit");
    kickBtn.classList.remove("hit");
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("blur", onHide);

  return () => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("blur", onHide);
    document.body.classList.remove("playing");
    ctrl.stop();
  };
}
